import React, {
  useState,
  useEffect,
  useContext,
  useMemo,
  useCallback
} from 'react';
import { string, bool, shape, number } from 'prop-types';
import { ExperienceContext, useStore, useMVCSID } from '@thd-nucleus/experience-context';
import { useLayoutGroupProps } from '@thd-olt-component-react/layout-manager';
import { withHydrator } from '@thd-olt-component-react/hydrator';
import { Carousel, Placeholder, Row } from '@thd-olt-component-react/core-ui';
import { ProductPod, ProductPodUtils } from '@thd-olt-component-react/product-pod';
import { withDynamicComponent } from '@thd-nucleus/app-render';
import {
  useDataModel,
  extend,
  params,
  string as dString,
  shape as dShape,
  arrayOf,
  QueryProvider,
} from '@thd-nucleus/data-sources';

import { SponsoredProductPod } from '../SponsoredProductPod';
import { makeApiRequest, shouldMakeApiRequest } from '../../utils/requestHelpers';
import { initImpressionWatchers } from '../../utils/initImpressionWatchers';
import {
  getSchema,
  getSkus,
  isExperienceDataResolved,
  mapSearchModel
} from '../../utils/helpers';

import './SponsoredCarousel.scss';

const propTypes = {
  customRenderId: string,
  slugId: string,
  hideTitle: bool,
  pageContext: shape({
    label: string,
    data: shape(),
    keyword: string,
    isSearch: bool,
    isCategory: bool,
    isBrowse: bool,
    schema: string,
    slugId: string
  }),
  slidesPer: number,
  noATCFulfillment: bool,
  utilizeRvData: bool,
  mockEnabledFeatureSwitch: bool,
  showSponsoredCarousel: bool,
};

const defaultProps = {
  customRenderId: '',
  slugId: '',
  hideTitle: false,
  pageContext: {},
  slidesPer: null,
  noATCFulfillment: null,
  utilizeRvData: false,
  mockEnabledFeatureSwitch: false,
  showSponsoredCarousel: false,
};
const SponsoredCarouselComponent = (props) => {
  const {
    customRenderId,
    slugId,
    pageContext,
    hideTitle,
    slidesPer,
    noATCFulfillment,
    mockEnabledFeatureSwitch,
    showSponsoredCarousel,
  } = props;
  const browserId = useMVCSID();
  const experienceContext = useContext(ExperienceContext);
  let { renderNewCarousel = false } = useLayoutGroupProps({ props });
  const { storeId, isLocalized } = useStore({
    varnish: true,
    online: true
  });
  const { utilizeRvData } = props;
  let breakpoints = {};
  if (!slidesPer) {
    breakpoints = Carousel.defaultProps.breakpoints;
  } else {
    breakpoints = {
      sm: { slidesPerView: slidesPer, slidesPerGroup: slidesPer },
      md: { slidesPerView: slidesPer, slidesPerGroup: slidesPer },
      lg: { slidesPerView: slidesPer, slidesPerGroup: slidesPer },
      xl: { slidesPerView: slidesPer, slidesPerGroup: slidesPer },
    };
  }

  const { schema, schemaId } = useMemo(() => {
    const schemaName = getSchema({ schema: pageContext?.schema, pageContext, props });
    return { schema: schemaName, schemaId: schemaName + '__carousel' };
  }, [props]);
  const isMobile = useMemo(() => (experienceContext.channel === 'mobile'), [experienceContext.channel]);
  const titleClassname = useMemo(() => {
    return `nucleus-carousel__title__text ${isMobile ? 'nucleus-carousel__title__text--medium' : ''}`;
  }, [isMobile]);

  const [lastRequestScenario, setLastRequestScenario] = useState({});
  const [pageId, setPageId] = useState('');
  const [products, setProducts] = useState([]);
  const [atcFilteredProducts, setATCFilteredProducts] = useState([]);

  const setAdProducts = useCallback((adProducts) => {
    if (adProducts && adProducts.length) {
      setProducts(adProducts
        .map((product) => ({
          ...product,
          onViewBeacon: product.onViewBeacon ? product.onViewBeacon.replace('https:', '') : '',
          ITC: `AUC-${product?.campaignId || ''}-${product?.placementId || ''}-${product?.slotId || ''}`
        }))
      );
    }
  }, []);

  if (showSponsoredCarousel
    || mockEnabledFeatureSwitch
    || schema === 'hp_sponsored_auth'
    || schema === 'hp_sponsored'
    || schema === 'cat_sponsored') {
    renderNewCarousel = true;
  }

  useEffect(() => {
    const isResolvedContext = isExperienceDataResolved({ isLocalized, experienceContext });

    if (renderNewCarousel && isResolvedContext) {

      if (pageContext?.label === 'browse-search'
        || pageContext?.isPip || pageContext?.isStorePage) {
        const isValidScenario = shouldMakeApiRequest({
          lastRequestScenario,
          experienceContext,
          pageContext,
          customRenderId

        });

        if (isValidScenario && browserId) {
          setLastRequestScenario({
            url: window.location.pathname + window.location.search,
            skus: getSkus(pageContext)
          });

          if (browserId) {
            makeApiRequest({
              browserId,
              schema,
              schemaId,
              experienceContext,
              pageContext,
              carouselType: pageContext?.isPip ? 'pip' : 'pla',
              setPageId,
              setAdProducts,
              utilizeRvData,
              slugId,
            });
          }

        }
      } else if (browserId) {
        // Personalized Homepage should by pass isValidScenarioß
        makeApiRequest({
          browserId,
          schema,
          schemaId,
          experienceContext,
          pageContext,
          carouselType: 'pla',
          setPageId,
          setAdProducts,
          utilizeRvData,
          slugId,
        });

      }
    }
  }, [pageContext?.data, isLocalized, experienceContext.mounted, slugId, browserId]);

  const skip = !products?.length;
  const resp = useDataModel('searchModel', {
    ssr: false,
    skip,
    variables: {
      itemIds: products?.map((item) => item.id.toString()) || [],
      storeId,
    },
  });

  const { data, loading, error } = resp;

  useEffect(() => {
    initImpressionWatchers(['NUCLEUS_CAROUSEL']);
  }, [data]);

  useEffect(() => {
    const filteredProducts = [];
    if (products && products.length && data?.searchModel?.products?.length) {
      products.forEach((product) => {
        const productData = data.searchModel.products.find(({ itemId }) => itemId === `${product.id}`);
        if (productData) {
          const isATCEnabled = ProductPodUtils.isATCEnabled(productData);
          if (isATCEnabled) {
            const updatedProduct = mapSearchModel(product, productData);
            filteredProducts.push(updatedProduct);
          }
        }
      });
      setATCFilteredProducts(filteredProducts);
    }
  }, [products, data]);

  if (renderNewCarousel && loading) {
    let slides = [];
    let textPlaceholder = [...Array(16)].map((value, idx) => <Placeholder type="text" key={idx.toString()} />);
    for (let i = 0; i < 16; i += 1) {
      slides.push(
        <div key={i.toString()} style={{ width: '100%' }}>
          <Placeholder
            type="rect"
            height="150px"
            width="100%"
          />
          {textPlaceholder}
        </div>
      );
    }
    return (
      <Row>
        <Row>
          <Carousel breakpoints={breakpoints}>
            {slides}
          </Carousel>
        </Row>
      </Row>
    );
  }

  return (
    <>
      {(atcFilteredProducts.length && renderNewCarousel) ? (
        <div id="sponsored-carousel-nucleus" className="col__12-12">
          <div
            id={schema}
            className="nucleus-carousel"
            data-testid="sponsored-pla-carousel"
          >
            <meta data-prop="name" content={schema} />
            {!hideTitle && (
              <div className="nucleus-carousel__title">
                <h2 className={titleClassname}>
                  Sponsored Products
                </h2>
              </div>
            )}
            <Carousel breakpoints={breakpoints}>
              {atcFilteredProducts.map((product, idx) => {
                return (
                  <SponsoredProductPod
                    key={product.id}
                    product={product}
                    pageId={pageId}
                    storeId={storeId}
                    position={idx + 1}
                    schema={schema}
                    noATCFulfillment={noATCFulfillment}
                  />
                );
              })}
            </Carousel>
          </div>
        </div>
      ) : null}
    </>
  );

};

SponsoredCarouselComponent.propTypes = propTypes;
SponsoredCarouselComponent.defaultProps = defaultProps;

SponsoredCarouselComponent.dataModel = {
  searchModel: params({
    itemIds: arrayOf(dString()),
  }).shape({
    products: arrayOf(dShape(ProductPod.dataModel.product))
  })
};

/* eslint react/jsx-props-no-spreading: 0 */
const QueryProvidedSponsoredCarousel = (props) => (
  <QueryProvider cacheKey="sponsored-nucleus-carousel">
    <SponsoredCarouselComponent {...props} />
  </QueryProvider>
);

QueryProvidedSponsoredCarousel.propTypes = propTypes;

QueryProvidedSponsoredCarousel.defaultProps = defaultProps;

QueryProvidedSponsoredCarousel.dataModel = extend({}, SponsoredCarouselComponent);

const SponsoredCarousel = withDynamicComponent(
  withHydrator(
    {
      id: 'sponsored-carousel-nucleus',
      preserveCtxVal: 'clientStore'
    },
    QueryProvidedSponsoredCarousel
  )
);

export { SponsoredCarousel };
